import type { Environment } from '../../environment';
import type {
  EntityObject, OverreactRequestShape,
} from '../../public-types';
import type { ResponseContract } from '../response-contract';
import { responseTypes } from '../response-types';
import { specTypes } from '../spec-types';
import { getSideEffectCacheStoreHelpers } from './sideEffectFnHelper';

export default function handler<
  TVariables extends object,
  TData,
  TEntity extends EntityObject,
  TResult extends TEntity | TEntity[] | null,
>(
  environment: Environment,
  processedResponse: TResult,
  request: OverreactRequestShape<TVariables, TData, TResult, TEntity>,
) {
  return (context: ResponseContract<TVariables, TData, TEntity, unknown, TResult>) => {
    const locator = 'locator' in request.variables ? request.variables.locator : undefined;
    if (!locator || typeof locator !== 'object') {
      throw new Error('Request variables must include a locator');
    }
    const { descriptor, order = [] } = locator as {
      descriptor: Record<string, string | number>;
      order: string[];
    };
    const { dataCb, spec } = request;
    const { requestContract, sideEffectFn } = spec;
    let parentId = requestContract.parentKeySelector?.(request.variables);

    if (context.responseType === responseTypes.COLL || spec.specType === specTypes.ADD) {
      if (!parentId && order.length > 0) {
        parentId = descriptor[order[order.length - 1]];
      }
      const entities = Array.isArray(processedResponse) ? processedResponse : [processedResponse];
      dataCb?.(entities.map(entity => context.applyId(entity as TEntity, parentId)), request);
    } else if (context.responseType === responseTypes.ENTITY) {
      if (!parentId && order.length > 1) {
        parentId = descriptor[order[order.length - 2]];
      }
      dataCb?.([context.applyId(processedResponse as TEntity, parentId)], request);
    } else if (context.responseType === responseTypes.NONE) {
      dataCb?.(null, request);
    }

    if (sideEffectFn) {
      sideEffectFn(processedResponse, request, spec, getSideEffectCacheStoreHelpers(environment));
    }
  };
}
